import { ChevronDown, Search } from 'lucide-react';
import { useState } from 'react';

import { Field } from '../PreviewFields';
import { PreviewHeader } from '../PreviewHeader';

// Preview / runtime screens for the combined Utility Bill Verification step's three
// connection-type methods (Electricity / PNG / LPG). Field keys match the gateway
// request bodies verbatim (electricity: consumer_id/service_provider/regMobileNo;
// PNG: ConsumerId/ServiceProvider/BpNo; LPG: LpgId) so the session runner can send
// them through unchanged.

// Electricity service providers — `value` is the exact code the gateway accepts
// (API.Models.Common.AllowedValueSets.ElectricityServiceProviders); `label` is the
// full board name SignCare's own product shows (GodigitelApp_Front getServiceProvider).
// The dropdown DISPLAYS the full name but SUBMITS the code.
export const ELECTRICITY_PROVIDERS: { value: string; label: string }[] = [
  { value: 'APSPDCL', label: 'Southern Power Distribution Company of Andhra Pradesh Limited' },
  { value: 'APEPDCL', label: 'Eastern Power Distribution Company of Andhra Pradesh Limited' },
  { value: 'APDCL', label: 'Assam Power Distribution Company Limited' },
  { value: 'NBPDCL', label: 'North Bihar Power Distribution Company Limited' },
  { value: 'SBPDCL', label: 'South Bihar Power Distribution Company Limited' },
  { value: 'CESC', label: 'Calcutta Electric Supply Corporation Limited' },
  { value: 'CH_ELEC', label: 'Chandigarh Electricity Department' },
  { value: 'CSPDCL', label: 'Chhattisgarh State Power Distribution Company Limited' },
  { value: 'DAMAN_DIU', label: 'Daman and Diu Electricity Department' },
  { value: 'TATA_DL', label: 'Tata Power Delhi Distribution' },
  { value: 'BSES_DL', label: 'BSES Yamuna Power Ltd / BSES Rajdhani Power Ltd' },
  { value: 'GOA_ELEC', label: 'Goa Electricity - for Tisvadi, Ponda & Verna' },
  { value: 'UPAY_GOA', label: 'Goa Electricity - for others' },
  { value: 'UGVCL', label: 'Uttar Gujarat VIJ Company Ltd.' },
  { value: 'PGVCL', label: 'Pashchim Gujarat VIJ Company Ltd.' },
  { value: 'MGVCL', label: 'Madhya Gujarat VIJ Company Limited' },
  { value: 'DGVCL', label: 'Dakshin Gujarat VIJ Company Limited' },
  { value: 'TORRENT_AHD', label: 'Torrent Power Limited - Ahmedabad' },
  { value: 'TORRENT_SURAT', label: 'Torrent Power Limited - Surat' },
  { value: 'TORRENT_AGRA', label: 'Torrent Power Limited - Agra' },
  { value: 'TORRENT_BHIWANDI', label: 'Torrent Power Limited - Bhiwandi' },
  { value: 'TORRENT_DAHEJ', label: 'Torrent Power Limited - Dahej' },
  { value: 'DHBVN', label: 'Dakshin Haryana Bijli Vitran Nigam' },
  { value: 'UHBVN', label: 'Uttar Haryana Bijli Vitran Nigam' },
  { value: 'HPSEB', label: 'Himachal Pradesh State Electricity Board Ltd' },
  { value: 'JBVNL', label: 'Jharkhand Bijli Vitran Nigam Limited' },
  { value: 'MESCOM', label: 'Mangalore Electricity Supply Company Limited' },
  { value: 'BESCOM', label: 'Bangalore Electricity Supply Company Ltd' },
  { value: 'CESCOM', label: 'Chamundeshwari Electricity Supply Company Limited - Mysore' },
  { value: 'GESCOM', label: 'Gulbarga Electricity Supply Company Limited' },
  { value: 'HESCOM', label: 'Hubli Electricity Supply Company Limited' },
  { value: 'KERALA', label: 'Kerala State Electricity Board' },
  { value: 'MPWZ', label: 'MP Paschim Kshetra Vidyut Vitran Company Limited' },
  { value: 'MPCZ', label: 'MP Madhya Kshetra Vidyut Vitran Company Limited' },
  { value: 'MPEZ', label: 'MP Poorv Kshetra Vidyut Vitran Company Limited' },
  { value: 'TATA_MUMBAI', label: 'TATA-Power - Mumbai' },
  {
    value: 'MAHAVITRAN',
    label: 'MAHAVITARAN - Maharashtra State Electricity Distribution Co. Ltd (MSEDCL)',
  },
  {
    value: 'BEST_MH',
    label: 'Brihanmumbai Electric Supply & Transport Undertaking for Greater Mumbai',
  },
  { value: 'MeECL', label: 'Meghalaya Energy Corporation Limited' },
  { value: 'ORISSA', label: 'Supply Company of Orissa Limited (North, South, West, Central)' },
  { value: 'PSPCL', label: 'Punjab State Power Corporation Limited' },
  { value: 'JAIPUR', label: 'Jaipur Vidhyut Vitran Nigam Ltd' },
  { value: 'AJMER', label: 'Ajmer Vidhyut Vitran Nigam Ltd' },
  { value: 'JODHPUR', label: 'Jodhpur Vidhyut Vitran Nigam Ltd' },
  { value: 'CHENNAI_NORTH', label: 'TANGEDCO - Chennai North' },
  { value: 'VILLUPURAM', label: 'TANGEDCO - Villupuram' },
  { value: 'COIMBATORE', label: 'TANGEDCO - Coimbatore' },
  { value: 'ERODE', label: 'TANGEDCO - Erode' },
  { value: 'MADURAI', label: 'TANGEDCO - Madurai' },
  { value: 'TRICHY', label: 'TANGEDCO - Trichy' },
  { value: 'TIRUNVELVEL', label: 'TANGEDCO - Tirunelveli' },
  { value: 'VELLORE', label: 'TANGEDCO - Vellore' },
  { value: 'CHENNAI_SOUTH', label: 'TANGEDCO - Chennai South' },
  { value: 'TSSPCL', label: 'The Southern Power Distribution Company of Telangana Limited' },
  { value: 'TRIPURA', label: 'Tripura State Electricity Corporation Limited' },
  { value: 'KESCO', label: 'Kanpur Electricity Supply Company' },
  { value: 'UPPCL', label: 'UP Vidyut Vitran Nigam Limited (All Zones)' },
  { value: 'WBENGAL', label: 'West Bengal State Electricity Distribution Company Limited' },
];

// PNG (Piped Natural Gas) service providers Signcare supports. `value` is the exact
// provider code the /png/verify API expects; `label` is the human name shown in the
// dropdown. Only Signcare-supported providers appear (currently Adani Gas). Add more
// entries here as Signcare enables them — no other change needed.
export const PNG_PROVIDERS: { value: string; label: string }[] = [
  { value: 'AG', label: 'Adani Gas (AG)' },
];

// Labelled dropdown styled to match the text Field primitive. Uses a custom
// (non-native) list so the options render INSIDE the phone preview frame and scroll
// within it, instead of the native <select> overlay spilling outside the device.
// The list only shows the values the Signcare API accepts (passed in `options`).
function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  options: readonly { value: string; label: string }[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  // Search over BOTH the full board name and the code so either works.
  const filtered = term
    ? options.filter(
        (o) => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term),
      )
    : options;
  // Show the full board name for the selected code (fall back to the code).
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;
  return (
    <div className="mb-2 flex flex-col gap-1">
      <span
        className="block text-[14px] font-medium"
        style={{ color: 'var(--brand-input-label, #000000)' }}
      >
        {label}
        <span className="text-red-500">*</span>
      </span>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setQuery('');
        }}
        style={{
          backgroundColor: 'var(--brand-input-fill, #ffffff)',
          borderColor: 'var(--brand-input-stroke, #E5E7EB)',
          borderRadius: 'var(--brand-input-radius, 0.5rem)',
        }}
        className="flex w-full items-center justify-between border px-3 py-2.5 text-left text-[length:var(--text-14)] outline-none"
      >
        <span className={value ? 'text-[#111827]' : 'text-[#A5A5A5]'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-[#9CA3AF] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-sm">
          {/* Type-to-filter search — only the Signcare-supported values are searched. */}
          <div className="flex items-center gap-1.5 border-b border-[#F3F4F6] px-2.5 py-1.5">
            <Search size={14} className="shrink-0 text-[#9CA3AF]" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full bg-transparent text-[length:var(--text-14)] text-[#111827] outline-none placeholder:text-[#A5A5A5]"
            />
          </div>
          <div className="max-h-[160px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-[length:var(--text-14)] text-[#9CA3AF]">
                No matches
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange?.(o.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`block w-full px-3 py-2 text-left text-[length:var(--text-14)] hover:bg-[#F3F4F6] ${
                    o.value === value ? 'bg-[#E5EDFF] text-primary' : 'text-[#111827]'
                  }`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Electricity ──
export interface ElectricityFormValues {
  consumer_id: string;
  service_provider: string;
  regMobileNo: string;
}
export function ElectricityScreen({
  values,
  onChange,
  errors,
}: {
  values?: ElectricityFormValues;
  onChange?: (patch: Partial<ElectricityFormValues>) => void;
  errors?: Partial<Record<keyof ElectricityFormValues, string>>;
}) {
  return (
    <>
      <PreviewHeader
        title="Electricity Bill Verification"
        subtitle="Enter your connection details"
      />
      <Field
        label="Consumer ID"
        placeholder="10123456789"
        hint="9–13 characters"
        value={values?.consumer_id}
        onChange={onChange ? (v) => onChange({ consumer_id: v }) : undefined}
        error={errors?.consumer_id}
      />
      <SelectField
        label="Service Provider"
        placeholder="Select your electricity board"
        options={ELECTRICITY_PROVIDERS}
        value={values?.service_provider}
        onChange={onChange ? (v) => onChange({ service_provider: v }) : undefined}
      />
      <Field
        label="Registered Mobile Number"
        placeholder="9876543210"
        inputMode="numeric"
        optional
        hint="Optional — 10 digits"
        value={values?.regMobileNo}
        onChange={onChange ? (v) => onChange({ regMobileNo: v }) : undefined}
        error={errors?.regMobileNo}
      />
    </>
  );
}

// ── PNG (Piped Natural Gas) ──
export interface PngFormValues {
  ConsumerId: string;
  ServiceProvider: string;
  BpNo: string;
}
export function PngScreen({
  values,
  onChange,
  errors,
}: {
  values?: PngFormValues;
  onChange?: (patch: Partial<PngFormValues>) => void;
  errors?: Partial<Record<keyof PngFormValues, string>>;
}) {
  return (
    <>
      <PreviewHeader
        title="PNG Connection Verification"
        subtitle="Enter your gas connection details"
      />
      <SelectField
        label="Service Provider"
        placeholder="Select your gas provider"
        options={PNG_PROVIDERS}
        value={values?.ServiceProvider}
        onChange={onChange ? (v) => onChange({ ServiceProvider: v }) : undefined}
      />
      <Field
        label="Consumer ID"
        placeholder="10000XXXXX"
        value={values?.ConsumerId}
        onChange={onChange ? (v) => onChange({ ConsumerId: v }) : undefined}
        error={errors?.ConsumerId}
      />
      <Field
        label="Business Partner Number"
        placeholder="BP number"
        optional
        hint="Optional"
        value={values?.BpNo}
        onChange={onChange ? (v) => onChange({ BpNo: v }) : undefined}
        error={errors?.BpNo}
      />
    </>
  );
}

// ── LPG ──
export interface LpgFormValues {
  LpgId: string;
}
export function LpgScreen({
  values,
  onChange,
  errors,
}: {
  values?: LpgFormValues;
  onChange?: (patch: Partial<LpgFormValues>) => void;
  errors?: Partial<Record<keyof LpgFormValues, string>>;
}) {
  return (
    <>
      <PreviewHeader title="LPG ID Verification" subtitle="Enter your LPG consumer ID" />
      <Field
        label="LPG ID"
        placeholder="1000000005XXXXXXX"
        inputMode="numeric"
        hint="17-digit LPG consumer ID"
        value={values?.LpgId}
        onChange={onChange ? (v) => onChange({ LpgId: v }) : undefined}
        error={errors?.LpgId}
      />
    </>
  );
}
